import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { publicApi } from "@/lib/api";
import PublicLayout from "@/components/public/PublicLayout";
import Markdown from "@/components/public/Markdown";
import { sanitizeHtml } from "@/lib/sanitize";
import "@/richtext.css";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate } from "@/lib/format";
import { Clock, ChevronLeft, ArrowRight, BookOpen } from "lucide-react";

export default function JournalArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    publicApi.get(`/public/journal/${slug}`)
      .then((r) => setA(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  // JSON-LD structured data (Article schema) for SEO
  useEffect(() => {
    if (!a) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.seo_title || a.title,
      description: a.seo_description || a.excerpt,
      image: a.cover_image ? [a.cover_image] : undefined,
      author: { "@type": "Person", name: a.author || "Le Collectif Horloger" },
      publisher: { "@type": "Organization", name: "Le Collectif Horloger" },
      datePublished: a.published_at,
      dateModified: a.updated_at || a.published_at,
      articleSection: a.category,
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "journal-jsonld";
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);
    return () => { const ex = document.getElementById("journal-jsonld"); if (ex) ex.remove(); };
  }, [a]);

  const canonical = typeof window !== "undefined" ? window.location.href : "";

  return (
    <PublicLayout>
      {a && (
        <>
          <title>{`${a.seo_title || a.title} · Le Collectif Horloger`}</title>
          <meta name="description" content={a.seo_description || a.excerpt || ""} />
          <link rel="canonical" href={canonical} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={a.seo_title || a.title} />
          <meta property="og:description" content={a.seo_description || a.excerpt || ""} />
          {a.cover_image && <meta property="og:image" content={a.cover_image} />}
        </>
      )}

      <article className="max-w-3xl mx-auto px-5 py-12">
        <button onClick={() => navigate("/journal")} className="flex items-center gap-1 text-sm text-[var(--text-2)] hover:text-[var(--text)] mb-8" data-testid="back-to-journal">
          <ChevronLeft size={16} /> Back to Journal
        </button>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-1/4 bg-[var(--panel-2)]" />
            <Skeleton className="h-10 w-3/4 bg-[var(--panel-2)]" />
            <Skeleton className="aspect-[16/9] w-full bg-[var(--panel-2)] rounded-lg" />
            <Skeleton className="h-40 w-full bg-[var(--panel-2)]" />
          </div>
        ) : notFound || !a ? (
          <div className="text-center text-[var(--muted)] py-24" data-testid="journal-notfound">
            <p className="mb-4">This article is no longer available.</p>
            <Link to="/journal" className="text-[#c9a86a] hover:text-[#b89455] transition-colors">Return to the Journal</Link>
          </div>
        ) : (
          <div data-testid="journal-article">
            {a.category && <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3 uppercase">{a.category}</div>}
            <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)] leading-tight" data-testid="article-title">{a.title}</h1>
            <div className="flex items-center gap-3 text-xs text-[var(--muted)] mt-4">
              {a.author && <span>By {a.author}</span>}
              {a.published_at && <span>· {fmtDate(a.published_at)}</span>}
              {a.reading_time && <span className="flex items-center gap-1">· <Clock size={12} /> {a.reading_time} min read</span>}
            </div>

            {a.cover_image && (
              <div className="aspect-[16/9] rounded-lg overflow-hidden border border-[var(--border-lch)] my-8">
                <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
              </div>
            )}

            {a.excerpt && <p className="text-lg text-[var(--text-2)] leading-relaxed mb-2">{a.excerpt}</p>}
            {a.body_format === "html"
              ? <div className="journal-html" data-testid="article-body-html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.body) }} />
              : <Markdown text={a.body} />}

            {a.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-[var(--border-lch)]">
                {a.tags.map((t) => <span key={t} className="text-[11px] px-2.5 py-1 rounded-full bg-[var(--panel-2)] text-[var(--text-2)] border border-[var(--border-2)]">#{t}</span>)}
              </div>
            )}

            {/* Related */}
            {a.related?.length > 0 && (
              <div className="mt-14" data-testid="journal-related">
                <h2 className="text-xl font-semibold text-[var(--text)] mb-5">Keep reading</h2>
                <div className="grid sm:grid-cols-3 gap-5">
                  {a.related.map((r) => (
                    <button key={r.id} onClick={() => navigate(`/journal/${r.slug}`)} className="group text-left bg-[var(--panel)] border border-[var(--border-lch)] rounded-lg overflow-hidden hover:border-[#c9a86a]/50 transition-colors" data-testid={`related-${r.id}`}>
                      <div className="aspect-[16/10] bg-[var(--panel-2)] overflow-hidden">
                        {r.cover_image ? <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full flex items-center justify-center text-[var(--muted)]"><BookOpen size={24} /></div>}
                      </div>
                      <div className="p-3"><div className="text-sm text-[var(--text)] leading-snug">{r.title}</div></div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-14 border border-[var(--border-lch)] rounded-lg p-8 bg-[var(--bg-2)] text-center">
              <h3 className="text-xl font-semibold text-[var(--text)]">Looking for a specific piece?</h3>
              <p className="text-[var(--text-2)] mt-2 text-sm">Join the VIP list for first calls on grails, or tell us exactly what you're hunting.</p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/vip")} data-testid="article-join-vip" className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none">Join the VIP List <ArrowRight size={16} className="ml-2" /></Button>
                <Button onClick={() => navigate("/request")} data-testid="article-request" variant="outline" className="border-[var(--border-2)] text-[var(--text)] hover:bg-[var(--panel-2)] rounded-none bg-transparent">Request a Watch</Button>
              </div>
            </div>
          </div>
        )}
      </article>
    </PublicLayout>
  );
}
